import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Circle, GraduationCap, ArrowRight, PlayCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

function Step({ step, done, onToggle }) {
  return (
    <div className={`border p-4 transition-colors ${done ? "border-emerald-500/30 bg-emerald-500/5" : "border-gold-500/15 bg-navy-900/40"}`} data-testid={`induction-step-${step.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={() => onToggle(step.id, !done)} data-testid={`induction-toggle-${step.id}`} className="mt-0.5 shrink-0">
          {done ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <Circle className="h-5 w-5 text-white/25 hover:text-gold-400 transition-colors" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${done ? "text-white/50 line-through" : "text-white"}`}>{step.title}</div>
          <p className="text-white/55 text-xs mt-1.5 leading-relaxed">{step.body}</p>
          {step.link && (
            <Link to={step.link} className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-xs mt-2" data-testid={`induction-link-${step.id}`}>
              Open it <ArrowRight className="h-3 w-3" />
            </Link>
          )}
          {step.videos && (
            <div className="grid md:grid-cols-2 gap-4 mt-3">
              {[["/FieldClaim_Product_Tour.mp4", "Product tour (2 min)"], ["/MDAu_Assessor_Mode_Walkthrough.mp4", "Assessor mode (3 min)"], ["/MDAu_EW_Prep_Walkthrough.mp4", "EW Prep scanner (2.5 min)"]].map(([src, label]) => (
                <div key={src}>
                  <Mono className="text-[0.5rem] text-white/40 mb-1.5 flex items-center gap-1.5"><PlayCircle className="h-3 w-3" /> {label}</Mono>
                  <video controls preload="metadata" className="w-full border border-gold-500/20 bg-black" data-testid={`induction-video-${label[0]}`}>
                    <source src={src} type="video/mp4" />
                  </video>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Induction() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [done, setDone] = useState([]);

  useEffect(() => {
    api.get("/induction").then((r) => { setData(r.data); setDone(r.data.done); }).catch(() => {});
  }, []);

  const toggle = async (id, val) => {
    setDone((d) => (val ? [...d, id] : d.filter((x) => x !== id)));
    try { const { data: r } = await api.post("/induction/step", { step_id: id, done: val }); setDone(r.done); } catch { /* keep optimistic */ }
  };

  if (!data) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold-400" /></div>;

  const pct = Math.round((done.length / data.total) * 100);

  return (
    <div className="space-y-8" data-testid="induction-page">
      <div>
        <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2"><GraduationCap className="h-3.5 w-3.5" /> Staff induction</Mono>
        <h1 className="font-heading text-3xl text-white mt-2">Welcome aboard{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</h1>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">Work through these modules in order — about 50 minutes all up. Tick each step as you go; your progress saves to your account. Practise everything on the demo claim, you can't break it.</p>
        <div className="mt-4 max-w-md">
          <div className="flex justify-between text-xs mb-1.5">
            <Mono className="text-[0.5rem] text-white/40">Progress</Mono>
            <span className="font-mono text-gold-300" data-testid="induction-progress">{done.length}/{data.total} · {pct}%</span>
          </div>
          <div className="h-1.5 bg-navy-800 border border-gold-500/15">
            <div className="h-full bg-gold-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {data.modules.map((m, i) => {
        const moduleDone = m.steps.every((s) => done.includes(s.id));
        return (
          <div key={m.key} data-testid={`induction-module-${m.key}`}>
            <div className="flex items-center gap-3 mb-3">
              <span className={`font-mono text-[0.6rem] border px-2 py-1 ${moduleDone ? "border-emerald-400/50 text-emerald-300" : "border-gold-500/30 text-gold-300"}`}>
                {moduleDone ? "✓" : `0${i + 1}`}
              </span>
              <h2 className="font-heading text-lg text-white">{m.title}</h2>
              <Mono className="text-[0.45rem] text-white/30 ml-auto">~{m.minutes} min</Mono>
            </div>
            <div className="space-y-2">
              {m.steps.map((s) => <Step key={s.id} step={s} done={done.includes(s.id)} onToggle={toggle} />)}
            </div>
          </div>
        );
      })}

      {pct === 100 && (
        <div className="border border-emerald-500/40 bg-emerald-500/10 p-6 text-center" data-testid="induction-complete">
          <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
          <div className="font-heading text-xl text-white">Induction complete.</div>
          <p className="text-white/55 text-sm mt-1">You know where everything lives. Next stop: the Task Board.</p>
        </div>
      )}
    </div>
  );
}
